import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Abas,
  Alerta,
  Bloco,
  BotaoDocumento,
  Campo,
  CAMPO_SIM,
  Cascata,
  Casos,
  type CasoPratico,
  Comparador,
  Contracheque,
  HeroModulo,
  Referencias,
  Regua,
  Tabela,
  abrirDocumento,
} from "../componentes/HubDpUi";
import {
  calcularHolerite,
  percentual,
  reais,
  type EntradaHolerite,
  type GrauInsalubridade,
  type ModeloIrrf,
  type ParametrosFolha,
} from "../lib/folhaDp";
import { reciboHolerite } from "../lib/documentosDp";
import {
  mascararMoeda,
  mascararInteiro,
  mascararCnpj,
  mascararCpf,
  mascararData,
  moedaDeNumero,
} from "../lib/mascaras";
import { REFERENCIA_HOLERITE } from "../componentes/HubDpTextos";

// O módulo Holerite: a folha do mês, o recibo, a referência e os casos práticos.
//
// É o módulo-RAIZ do HUB DP: Férias/13º, Encargos e eSocial todos usam o cálculo
// daqui. Foi o primeiro portado e o primeiro redesenhado, e o desenho desta tela é
// o que os outros quatro repetem — hero navy com o número herói, formulário à
// esquerda, resultado à direita, e as peças visuais (cascata, régua, comparador)
// no lugar de listas.

const BASE = "/ferramenta/hub-dp/holerite";
const ABAS = [
  { slug: "", rotulo: "Calculadora" },
  { slug: "recibo", rotulo: "Recibo" },
  { slug: "referencia", rotulo: "Referência" },
  { slug: "casos", rotulo: "Casos práticos" },
];

/**
 * O formulário guarda TEXTO (o que o campo mostra) e o cálculo recebe NÚMERO.
 *
 * As duas coisas separadas, e a conversão numa fronteira só — a lição da máscara
 * de moeda: guardar `"R$ 1.234,56"` e mandar isso ao cálculo daria `NaN`, e o
 * holerite sairia zerado sem erro nenhum na tela.
 */
interface Formulario {
  salarioBase: string;
  horasExtras: string;
  horasNoturnas: string;
  periculosidade: boolean;
  insalubridade: GrauInsalubridade;
  faltas: string;
  valeTransporte: boolean;
  dependentes: string;
  modeloIrrf: ModeloIrrf;
  // Os campos do recibo — só de identificação, não entram na conta.
  empresa: string;
  cnpj: string;
  empregado: string;
  cpf: string;
  cargo: string;
  admissao: string;
  competencia: string;
}

const VAZIO: Formulario = {
  salarioBase: "",
  horasExtras: "",
  horasNoturnas: "",
  periculosidade: false,
  insalubridade: "nenhuma",
  faltas: "",
  valeTransporte: false,
  dependentes: "",
  modeloIrrf: "completo",
  empresa: "",
  cnpj: "",
  empregado: "",
  cpf: "",
  cargo: "",
  admissao: "",
  competencia: "",
};

const numeroDe = (texto: string): number => mascararMoeda(texto).numero ?? 0;
const inteiroDe = (texto: string): number => Number(texto.replace(/\D+/g, "")) || 0;

function paraEntrada(f: Formulario): EntradaHolerite {
  return {
    salarioBase: numeroDe(f.salarioBase),
    horasExtras: inteiroDe(f.horasExtras),
    horasNoturnas: inteiroDe(f.horasNoturnas),
    periculosidade: f.periculosidade,
    insalubridade: f.insalubridade,
    faltas: inteiroDe(f.faltas),
    valeTransporte: f.valeTransporte,
    dependentes: inteiroDe(f.dependentes),
    modeloIrrf: f.modeloIrrf,
  };
}

/**
 * Os casos práticos.
 *
 * Ficam AQUI, e não em `HubDpTextos`, porque o tipo deles é o `Formulario` deste
 * arquivo — importá-lo de lá criaria um ciclo de import. E o caso prático é do
 * módulo: é aqui que ele é coeso.
 *
 * Cada um carrega a calculadora em vez de mostrar o resultado pronto, que é o
 * desenho da origem e é o certo: o aluno vê os campos preenchidos e mexe, que é
 * onde se aprende.
 */
const CASOS_HOLERITE: CasoPratico<Partial<Formulario>>[] = [
  {
    nome: "Salário de R$ 3.000, sem adicionais",
    descricao:
      "O caso mais simples, para ver o INSS na faixa de 12% e a redução do IR de 2026 zerando o imposto.",
    valores: { salarioBase: moedaDeNumero(3000), modeloIrrf: "completo" },
  },
  {
    nome: "Com periculosidade e horas extras",
    descricao:
      "R$ 3.000 de base, 10 horas extras, 20 noturnas e periculosidade de 30%. Repare na barra: os adicionais viram metade do bruto.",
    valores: {
      salarioBase: moedaDeNumero(3000),
      horasExtras: "10",
      horasNoturnas: "20",
      periculosidade: true,
      valeTransporte: true,
      dependentes: "1",
    },
  },
  {
    nome: "Salário acima do teto do INSS",
    descricao:
      "R$ 12.000 de base. A régua mostra o marcador passando do teto, o INSS para de crescer, e o Modelo 1 volta a ganhar do simplificado.",
    valores: { salarioBase: moedaDeNumero(12000), modeloIrrf: "completo" },
  },
  {
    nome: "Insalubridade em grau máximo",
    descricao:
      "Salário mínimo com adicional de 40%. O adicional sai do MÍNIMO, não do salário do empregado — é a pegadinha do módulo.",
    valores: { salarioBase: moedaDeNumero(1621), insalubridade: "maximo" },
  },
  {
    nome: "Três faltas injustificadas",
    descricao:
      "R$ 2.400 com 3 faltas. O desconto é salário ÷ 30 por dia; o reflexo no DSR não está incluído nesta versão.",
    valores: { salarioBase: moedaDeNumero(2400), faltas: "3" },
  },
  {
    nome: "Com dois dependentes",
    descricao:
      "R$ 5.000 e 2 dependentes. Compare os dois modelos: com dependentes o completo passa a valer mais que o simplificado.",
    valores: { salarioBase: moedaDeNumero(5000), dependentes: "2", modeloIrrf: "completo" },
  },
];

export default function DpHolerite({ p, aba }: { p: ParametrosFolha; aba: string }) {
  const [f, setF] = useState<Formulario>(VAZIO);
  const [popupBloqueado, setPopupBloqueado] = useState(false);
  const navegar = useNavigate();

  const entrada = paraEntrada(f);
  const temSalario = entrada.salarioBase > 0;
  const r = temSalario ? calcularHolerite(entrada, p) : null;

  const troca = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) =>
    setF((atual) => ({ ...atual, [campo]: valor }));

  return (
    <>
      <HeroModulo
        titulo="Holerite"
        subtitulo="A folha do mês: proventos, INSS progressivo, IRRF nos dois modelos e o FGTS."
        heroi={
          r
            ? {
                rotulo: "Líquido a receber",
                valor: reais(r.liquido),
                detalhe: `${reais(r.proventos.total)} de proventos − ${reais(r.descontos.total)} de descontos`,
              }
            : null
        }
        vazio="Informe o salário base e o líquido aparece aqui, conforme você digita."
      />
      <Abas base={BASE} abas={ABAS} />

      {aba === "" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
          <Formulario_ f={f} troca={troca} />
          <Resultado r={r} p={p} f={f} troca={troca} />
        </div>
      )}

      {aba === "recibo" && (
        <ReciboAba
          f={f}
          troca={troca}
          r={r}
          p={p}
          bloqueado={popupBloqueado}
          setBloqueado={setPopupBloqueado}
        />
      )}

      {aba === "referencia" && <Referencia p={p} />}

      {aba === "casos" && (
        <Casos
          casos={CASOS_HOLERITE}
          aoCarregar={(valores) => {
            setF({ ...VAZIO, ...valores });
            navegar(BASE);
          }}
        />
      )}

      <Referencias inss={p.referenciaInss} irrf={p.referenciaIrrf} />
    </>
  );
}

// ---------------------------------------------------------------------------
// O formulário
// ---------------------------------------------------------------------------

function Formulario_({
  f,
  troca,
}: {
  f: Formulario;
  troca: <K extends keyof Formulario>(campo: K, valor: Formulario[K]) => void;
}) {
  return (
    // `id` nomeado porque a prova de tela precisa de ESCOPO: os rótulos "Faltas" e
    // "Dependentes" aparecem também nos outros módulos, e seletor por texto sem
    // escopo pega o elemento errado — a cicatriz das sete etiquetas iguais na
    // calculadora do IR.
    <form
      id="folha-do-mes"
      className="surface h-fit space-y-5 p-5 lg:sticky lg:top-6"
      onSubmit={(e) => e.preventDefault()}
    >
      <Campo rotulo="Salário base" ajuda="O salário contratual do mês, sem adicionais.">
        <input
          className={CAMPO_SIM}
          inputMode="numeric"
          placeholder="R$ 0,00"
          value={f.salarioBase}
          onChange={(e) => troca("salarioBase", mascararMoeda(e.target.value).texto)}
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Horas extras (50%)" ajuda="Quantidade de horas extras no mês.">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="0"
            value={f.horasExtras}
            onChange={(e) => troca("horasExtras", mascararInteiro(e.target.value, 3))}
          />
        </Campo>
        <Campo
          rotulo="Horas noturnas (20%)"
          ajuda="Horas trabalhadas entre 22h e 5h. O adicional é de 20% sobre a hora normal."
        >
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="0"
            value={f.horasNoturnas}
            onChange={(e) => troca("horasNoturnas", mascararInteiro(e.target.value, 3))}
          />
        </Campo>
        <Campo rotulo="Faltas injustificadas (dias)" ajuda="Desconto de salário ÷ 30 por dia.">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="0"
            value={f.faltas}
            onChange={(e) => troca("faltas", mascararInteiro(e.target.value, 2))}
          />
        </Campo>
        <Campo rotulo="Dependentes" ajuda="Para a dedução do IRRF no Modelo 1.">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="0"
            value={f.dependentes}
            onChange={(e) => troca("dependentes", mascararInteiro(e.target.value, 2))}
          />
        </Campo>
      </div>

      <Campo
        rotulo="Insalubridade"
        ajuda="O adicional incide sobre o salário mínimo, não sobre o salário do empregado."
      >
        <select
          className={CAMPO_SIM}
          value={f.insalubridade}
          onChange={(e) => troca("insalubridade", e.target.value as GrauInsalubridade)}
        >
          <option value="nenhuma">Nenhuma</option>
          <option value="minimo">Grau mínimo (10%)</option>
          <option value="medio">Grau médio (20%)</option>
          <option value="maximo">Grau máximo (40%)</option>
        </select>
      </Campo>

      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-semibold text-cinza">Outros</legend>
        <label className="flex items-center gap-2.5 text-sm text-tinta">
          <input
            type="checkbox"
            className="size-4 accent-laranja"
            checked={f.periculosidade}
            onChange={(e) => troca("periculosidade", e.target.checked)}
          />
          Periculosidade (30% do salário base)
        </label>
        <label className="flex items-center gap-2.5 text-sm text-tinta">
          <input
            type="checkbox"
            className="size-4 accent-laranja"
            checked={f.valeTransporte}
            onChange={(e) => troca("valeTransporte", e.target.checked)}
          />
          Descontar vale-transporte (6% do salário base)
        </label>
      </fieldset>

      <fieldset>
        <legend className="mb-1.5 text-xs font-semibold text-cinza">Modelo do IRRF</legend>
        <div className="space-y-2">
          {(
            [
              ["completo", "Modelo 1 — completo", "Abate o INSS e a dedução por dependente."],
              [
                "simplificado",
                "Modelo 2 — simplificado",
                "Desconto único que substitui todas as deduções legais, o INSS incluído.",
              ],
            ] as const
          ).map(([valor, rotulo, explica]) => (
            <label key={valor} className="flex gap-2.5 text-sm text-tinta">
              <input
                type="radio"
                name="modelo-irrf-holerite"
                className="mt-0.5 size-4 shrink-0 accent-laranja"
                checked={f.modeloIrrf === valor}
                onChange={() => troca("modeloIrrf", valor)}
              />
              <span>
                <span className="font-semibold">{rotulo}</span>
                <span className="block text-xs text-cinza">{explica}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </form>
  );
}

// ---------------------------------------------------------------------------
// O resultado
// ---------------------------------------------------------------------------

function Resultado({
  r,
  p,
  f,
  troca,
}: {
  r: ReturnType<typeof calcularHolerite> | null;
  p: ParametrosFolha;
  f: Formulario;
  troca: <K extends keyof Formulario>(campo: K, valor: Formulario[K]) => void;
}) {
  if (!r) {
    return (
      <div className="grid place-items-center rounded-cf-lg border border-dashed border-borda p-10 text-center">
        <div className="max-w-sm">
          <p className="font-display text-lg font-bold text-titulo">A folha aparece aqui</p>
          <p className="mt-1.5 text-sm leading-relaxed text-cinza">
            Informe o salário base à esquerda. Os valores mudam conforme você digita, e nada é
            gravado — nem aqui, nem no servidor.
          </p>
        </div>
      </div>
    );
  }

  const vantajoso = r.irrf.vantajoso === "completo" ? "Modelo 1" : "Modelo 2";

  return (
    <div id="folha-calculada" className="space-y-5">
      {/* O contracheque em duas colunas: é a forma que o contador reconhece. */}
      <Contracheque
        proventos={[
          { rotulo: "Salário base", valor: reais(r.proventos.salarioBase) },
          ...(r.proventos.horaExtra > 0
            ? [
                {
                  rotulo: "Horas extras 50%",
                  valor: reais(r.proventos.horaExtra),
                  ajuda: `hora de ${reais(r.valorHora)} × 1,5`,
                },
              ]
            : []),
          ...(r.proventos.adicionalNoturno > 0
            ? [{ rotulo: "Adicional noturno 20%", valor: reais(r.proventos.adicionalNoturno) }]
            : []),
          ...(r.proventos.periculosidade > 0
            ? [{ rotulo: "Periculosidade 30%", valor: reais(r.proventos.periculosidade) }]
            : []),
          ...(r.proventos.insalubridade > 0
            ? [
                {
                  rotulo: "Insalubridade",
                  valor: reais(r.proventos.insalubridade),
                  ajuda: `sobre o salário mínimo de ${reais(p.salarioMinimo)}`,
                },
              ]
            : []),
          ...(r.proventos.descontoFaltas > 0
            ? [
                {
                  rotulo: "Faltas (dedução)",
                  valor: `− ${reais(r.proventos.descontoFaltas)}`,
                  ajuda: "salário ÷ 30 por dia. Não inclui o reflexo no DSR.",
                },
              ]
            : []),
        ]}
        descontos={[
          {
            rotulo: "INSS",
            valor: reais(r.descontos.inss),
            ajuda: r.inss.noTeto
              ? `no teto — alíquota efetiva de ${percentual(r.inss.aliquotaEfetiva)}`
              : `faixa de ${percentual(r.inss.aliquotaFaixa)} — efetiva de ${percentual(r.inss.aliquotaEfetiva)}`,
          },
          ...(r.descontos.irrf > 0 || r.irrf.aplicado.impostoTabela > 0
            ? [
                {
                  rotulo: "IRRF",
                  valor: reais(r.descontos.irrf),
                  ajuda:
                    r.irrf.aplicado.reducao > 0
                      ? `${reais(r.irrf.aplicado.impostoTabela)} de tabela − ${reais(r.irrf.aplicado.reducao)} de redução`
                      : `base de ${reais(r.irrf.aplicado.base)}`,
                },
              ]
            : []),
          ...(r.descontos.valeTransporte > 0
            ? [{ rotulo: "Vale-transporte 6%", valor: reais(r.descontos.valeTransporte) }]
            : []),
        ]}
        totalProventos={reais(r.proventos.total)}
        totalDescontos={reais(r.descontos.total)}
        liquido={reais(r.liquido)}
        rodape={
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 text-sm">
            <span className="text-cinza">
              FGTS depositado{" "}
              <span className="text-xs">(do empregador — NÃO é desconto e não reduz o líquido)</span>
            </span>
            <span className="font-bold tabular-nums text-sucesso">{reais(r.fgts)}</span>
          </div>
        }
      />

      {/* A cascata: o que a lista de números não mostra. */}
      <Bloco titulo="Como o bruto se reparte">
        <Cascata
          rotuloTotal="Total de proventos"
          total={r.proventos.total}
          fatias={[
            { rotulo: "Líquido para o empregado", valor: r.liquido, cor: "sucesso" },
            {
              rotulo: "INSS retido",
              valor: r.descontos.inss,
              cor: "erro",
              detalhe: "vai para a Previdência",
            },
            ...(r.descontos.irrf > 0
              ? [
                  {
                    rotulo: "IRRF retido",
                    valor: r.descontos.irrf,
                    cor: "erro" as const,
                    detalhe: "vai para a Receita",
                  },
                ]
              : []),
            ...(r.descontos.valeTransporte > 0
              ? [
                  {
                    rotulo: "Vale-transporte",
                    valor: r.descontos.valeTransporte,
                    cor: "cinza" as const,
                  },
                ]
              : []),
          ]}
        />
      </Bloco>

      {/* A régua: substitui "por que caí nesta alíquota?" por uma imagem. */}
      <Bloco titulo="Onde este salário caiu na tabela">
        <div className="space-y-6">
          <Regua
            rotulo={`INSS — ${p.referenciaInss}`}
            degraus={p.faixas.inss}
            valor={r.inss.baseAplicada}
            teto={p.inssTeto}
            formatar={reais}
          />
          <Regua
            rotulo={`IRRF — base de cálculo, ${p.referenciaIrrf}`}
            degraus={p.faixas.irrf}
            valor={r.irrf.aplicado.base}
            formatar={reais}
          />
        </div>
        {r.inss.noTeto && (
          <p className="mt-3 text-xs leading-relaxed text-alerta-tinta">
            A remuneração passou do teto de {reais(p.inssTeto)}: o INSS parou de crescer em{" "}
            {reais(p.inssDescontoMaximo)}, e é por isso que a alíquota efetiva caiu para{" "}
            {percentual(r.inss.aliquotaEfetiva)}.
          </p>
        )}
      </Bloco>

      {/* O comparador: clicável, porque a escolha é do aluno. */}
      <Bloco
        titulo="Os dois modelos de IRRF"
        acao={
          r.irrf.vantajoso !== f.modeloIrrf ? (
            <button
              type="button"
              onClick={() => troca("modeloIrrf", r.irrf.vantajoso)}
              className="btn-contorno text-xs"
            >
              Usar o {vantajoso}
            </button>
          ) : undefined
        }
      >
        <Comparador
          aoEscolher={(chave) => troca("modeloIrrf", chave as ModeloIrrf)}
          // Com a redução de 2026, todo salário até cerca de R$ 4.000 zera o
          // imposto nos DOIS modelos — e aí coroar um deles ensinaria uma escolha
          // que não existe. O empate explica por que os dois deram zero.
          empate={
            r.irrf.completo.valor === r.irrf.simplificado.valor
              ? r.irrf.completo.valor === 0
                ? `A redução do IR de 2026 (${reais(r.irrf.aplicado.reducao)}) é maior que o imposto que a tabela cobraria nos dois casos, então não há retenção — e a escolha do modelo não muda nada nesta faixa de salário.`
                : "Nesta configuração os dois modelos levam ao mesmo imposto, então a escolha é indiferente."
              : undefined
          }
          opcoes={[
            {
              chave: "completo",
              titulo: "Modelo 1 — completo",
              valor: reais(r.irrf.completo.valor),
              detalhe: `base de ${reais(r.irrf.completo.base)} · ${reais(r.irrf.completo.impostoTabela)} pela tabela, antes da redução`,
              vencedora: r.irrf.vantajoso === "completo",
              escolhida: f.modeloIrrf === "completo",
            },
            {
              chave: "simplificado",
              titulo: "Modelo 2 — simplificado",
              valor: reais(r.irrf.simplificado.valor),
              detalhe: `base de ${reais(r.irrf.simplificado.base)} · ${reais(r.irrf.simplificado.impostoTabela)} pela tabela, antes da redução`,
              vencedora: r.irrf.vantajoso === "simplificado",
              escolhida: f.modeloIrrf === "simplificado",
            },
          ]}
        />
        {r.irrf.aplicado.reducao > 0 && (
          <p className="mt-3 text-xs leading-relaxed text-cinza">
            Os dois já vêm com a <b className="text-tinta">redução do IR de 2026</b> de{" "}
            {reais(r.irrf.aplicado.reducao)}, que incide sobre os rendimentos tributáveis do mês e
            abate o imposto apurado pela tabela.
          </p>
        )}
      </Bloco>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A aba de referência
// ---------------------------------------------------------------------------

function Referencia({ p }: { p: ParametrosFolha }) {
  return (
    <div className="space-y-7">
      {/* As tabelas de faixas vêm do BANCO, não de texto cravado: é a mesma fonte
          que o cálculo usa, então não há como uma discordar da outra. E acima delas
          a régua, porque a imagem responde antes da tabela. */}
      <Bloco titulo="As duas tabelas progressivas, em escala">
        <div className="space-y-7">
          <Regua
            rotulo={`INSS — ${p.referenciaInss}`}
            degraus={p.faixas.inss}
            valor={0}
            teto={p.inssTeto}
            formatar={reais}
          />
          <Regua
            rotulo={`IRRF — ${p.referenciaIrrf}`}
            degraus={p.faixas.irrf}
            valor={0}
            formatar={reais}
          />
        </div>
        <p className="mt-4 text-xs leading-relaxed text-cinza">
          A largura de cada degrau é o intervalo que ele cobre, e a intensidade cresce com a
          alíquota. A última faixa do IRRF <b className="text-tinta">não tem teto</b>: a largura dela
          no desenho é convenção, não dado.
        </p>
      </Bloco>

      <Tabela
        tabela={{
          titulo: `INSS progressivo — ${p.referenciaInss}`,
          colunas: ["Faixa (até)", "Alíquota", "Parcela a deduzir"],
          linhas: p.faixas.inss.map((x) => [
            x.limite === null ? "sem teto" : reais(x.limite),
            percentual(x.aliquota),
            reais(x.parcelaDeduzir),
          ]),
          nota:
            `Teto do salário de contribuição: ${reais(p.inssTeto)}. Acima dele a contribuição para ` +
            `de crescer e fica em ${reais(p.inssDescontoMaximo)} — por isso a alíquota efetiva cai ` +
            `conforme o salário sobe.`,
        }}
      />
      <Tabela
        tabela={{
          titulo: `IRRF mensal — ${p.referenciaIrrf}`,
          colunas: ["Base (até)", "Alíquota", "Parcela a deduzir"],
          linhas: p.faixas.irrf.map((x) => [
            x.limite === null ? "sem teto" : reais(x.limite),
            percentual(x.aliquota),
            reais(x.parcelaDeduzir),
          ]),
          nota:
            `Dedução por dependente: ${reais(p.deducaoDependente)} (só no Modelo 1). ` +
            `Desconto simplificado: ${reais(p.descontoSimplificado)} (Modelo 2).`,
        }}
      />
      <Tabela
        tabela={{
          titulo: "Redução mensal do IRPF (reforma de 2026)",
          colunas: ["Rendimentos tributáveis", "Redução"],
          linhas: [
            [`até ${reais(p.reducaoIr.limiteIntegral)}`, reais(p.reducaoIr.valorIntegral)],
            [
              `de ${reais(p.reducaoIr.limiteIntegral)} a ${reais(p.reducaoIr.limiteFinal)}`,
              `${reais(p.reducaoIr.base)} − ${p.reducaoIr.coeficiente.toLocaleString("pt-BR", { maximumFractionDigits: 6 })} × rendimento`,
            ],
            [`acima de ${reais(p.reducaoIr.limiteFinal)}`, reais(0)],
          ],
          nota:
            "A redução abate o imposto apurado pela tabela e incide sobre os rendimentos " +
            "tributáveis do mês (o bruto), não sobre a base pós-INSS. Nunca torna o imposto " +
            "negativo. É aplicada no holerite; férias e 13º seguem com o imposto puro de tabela.",
        }}
      />
      {REFERENCIA_HOLERITE.map((t) => (
        <Tabela key={t.titulo} tabela={t} />
      ))}
      <Alerta tom="atencao" titulo="O FGTS não é desconto">
        Os 8% do FGTS são depósito do empregador na conta vinculada do empregado. Eles não saem do
        salário, e por isso não entram no total de descontos nem reduzem o líquido — é a confusão
        mais comum de quem está começando.
      </Alerta>
      <Alerta tom="informacao" titulo="A base da insalubridade">
        O adicional de insalubridade incide sobre o <strong>salário mínimo</strong> (
        {reais(p.salarioMinimo)}), não sobre o salário do empregado. A Súmula Vinculante 4 do STF
        declarou essa base inconstitucional, mas manteve sua aplicação até que lei ou convenção
        disponha de outro modo — então o mínimo segue sendo o padrão. Confira a CCT da categoria:
        algumas fixam base própria.
      </Alerta>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A aba do recibo
// ---------------------------------------------------------------------------

function ReciboAba({
  f,
  troca,
  r,
  p,
  bloqueado,
  setBloqueado,
}: {
  f: Formulario;
  troca: <K extends keyof Formulario>(campo: K, valor: Formulario[K]) => void;
  r: ReturnType<typeof calcularHolerite> | null;
  p: ParametrosFolha;
  bloqueado: boolean;
  setBloqueado: (v: boolean) => void;
}) {
  if (!r) {
    return (
      <Alerta tom="atencao" titulo="Calcule a folha primeiro">
        O recibo usa os valores da aba <strong>Calculadora</strong>. Informe ao menos o salário base
        por lá e volte aqui.
      </Alerta>
    );
  }

  const gerar = () => {
    // A data de emissão vem daqui, da TELA — os documentos são puros e não leem o
    // relógio, para poderem ser provados com qualquer data.
    const hoje = new Date().toLocaleDateString("pt-BR");
    const proventos = [
      { rotulo: "Salário base", valor: reais(r.proventos.salarioBase) },
      ...(r.proventos.horaExtra > 0
        ? [{ rotulo: "Horas extras 50%", valor: reais(r.proventos.horaExtra) }]
        : []),
      ...(r.proventos.adicionalNoturno > 0
        ? [{ rotulo: "Adicional noturno 20%", valor: reais(r.proventos.adicionalNoturno) }]
        : []),
      ...(r.proventos.periculosidade > 0
        ? [{ rotulo: "Periculosidade 30%", valor: reais(r.proventos.periculosidade) }]
        : []),
      ...(r.proventos.insalubridade > 0
        ? [{ rotulo: "Insalubridade", valor: reais(r.proventos.insalubridade) }]
        : []),
      ...(r.proventos.descontoFaltas > 0
        ? [{ rotulo: "Faltas (dedução)", valor: `− ${reais(r.proventos.descontoFaltas)}` }]
        : []),
    ];
    const descontos = [
      { rotulo: "INSS", valor: reais(r.descontos.inss) },
      ...(r.descontos.irrf > 0 ? [{ rotulo: "IRRF", valor: reais(r.descontos.irrf) }] : []),
      ...(r.descontos.valeTransporte > 0
        ? [{ rotulo: "Vale-transporte 6%", valor: reais(r.descontos.valeTransporte) }]
        : []),
    ];
    const ok = abrirDocumento(
      reciboHolerite({
        empresa: f.empresa,
        cnpj: f.cnpj,
        empregado: f.empregado,
        cpf: f.cpf,
        cargo: f.cargo,
        admissao: f.admissao,
        competencia: f.competencia,
        dataEmissao: hoje,
        proventos,
        descontos,
        totalProventos: reais(r.proventos.total),
        totalDescontos: reais(r.descontos.total),
        liquido: reais(r.liquido),
        baseInss: reais(r.inss.baseAplicada),
        baseIrrf: reais(r.irrf.aplicado.base),
        fgts: reais(r.fgts),
        referencia: `INSS — ${p.referenciaInss}; IRRF — ${p.referenciaIrrf}`,
      }),
    );
    setBloqueado(!ok);
  };

  return (
    <div className="space-y-6">
      <Alerta tom="informacao">
        Os campos abaixo só aparecem no recibo — eles não entram na conta. Os valores vêm da aba
        Calculadora, e o líquido é <b>{reais(r.liquido)}</b>.
      </Alerta>

      <form
        id="dados-do-recibo"
        className="surface grid gap-4 p-5 sm:grid-cols-2"
        onSubmit={(e) => e.preventDefault()}
      >
        <Campo rotulo="Empresa">
          <input
            className={CAMPO_SIM}
            value={f.empresa}
            onChange={(e) => troca("empresa", e.target.value)}
          />
        </Campo>
        <Campo rotulo="CNPJ">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={f.cnpj}
            onChange={(e) => troca("cnpj", mascararCnpj(e.target.value))}
          />
        </Campo>
        <Campo rotulo="Empregado">
          <input
            className={CAMPO_SIM}
            value={f.empregado}
            onChange={(e) => troca("empregado", e.target.value)}
          />
        </Campo>
        <Campo rotulo="CPF">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            value={f.cpf}
            onChange={(e) => troca("cpf", mascararCpf(e.target.value))}
          />
        </Campo>
        <Campo rotulo="Cargo">
          <input
            className={CAMPO_SIM}
            value={f.cargo}
            onChange={(e) => troca("cargo", e.target.value)}
          />
        </Campo>
        <Campo rotulo="Admissão">
          <input
            className={CAMPO_SIM}
            inputMode="numeric"
            placeholder="dd/mm/aaaa"
            value={f.admissao}
            onChange={(e) => troca("admissao", mascararData(e.target.value))}
          />
        </Campo>
        <Campo rotulo="Competência" ajuda="O mês a que a folha se refere.">
          <input
            className={CAMPO_SIM}
            placeholder="03/2026"
            value={f.competencia}
            onChange={(e) => troca("competencia", e.target.value.slice(0, 7))}
          />
        </Campo>
      </form>

      <BotaoDocumento rotulo="Gerar o recibo" aoClicar={gerar} bloqueado={bloqueado} />
      <p className="text-xs leading-relaxed text-cinza">
        O recibo abre em outra aba, pronto para imprimir. Para salvar em PDF, escolha “Salvar como
        PDF” na janela de impressão do navegador.
      </p>
    </div>
  );
}

/** Reexportado para os casos práticos e para a prova de tela. */
export { VAZIO as FORMULARIO_VAZIO, moedaDeNumero };
export type { Formulario as FormularioHolerite };
